import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import useToast from "../../../../hooks/useToast";
import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import { useUploadFormDocument } from "../../hooks/mutations/client-forms.mutations";

interface UploadFormDocumentFormProps {
    clientId: string;
    formId: string;
    onFormSubmit: () => void;
}

export default function UploadFormDocumentForm({
    clientId,
    formId,
    onFormSubmit,
}: UploadFormDocumentFormProps) {
    const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(
        null
    );

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const uploadMutation = useUploadFormDocument();

    const onDrop = React.useCallback((acceptedFiles: File[]) => {
        setSelectedFiles((prev) =>
            prev ? [...prev, ...acceptedFiles] : [...acceptedFiles]
        );
    }, []);

    const handleRemoveSelectedFile = (filename: string) => {
        if (!selectedFiles) return;
        const updated = selectedFiles.filter((f) => f.name !== filename);
        setSelectedFiles(updated.length > 0 ? updated : null);
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!selectedFiles || selectedFiles.length === 0) {
            toast({
                mode: "error",
                message: "Please select file(s) to upload",
            });
            return;
        }

        const formData = new FormData();
        selectedFiles.forEach((file) => formData.append("documents", file));

        uploadMutation.mutate(
            { clientId, formId, formData },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message:
                            res.message ||
                            `${selectedFiles.length} document${
                                selectedFiles.length > 1 ? "s" : ""
                            } uploaded successfully`,
                    });
                    queryClient.invalidateQueries(["client-forms"]);
                    setSelectedFiles(null);
                    onFormSubmit();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err?.response?.data?.message ||
                            "Could not upload documents at this time",
                    });
                },
            }
        );
    };

    return (
        <form
            className="space-y-4"
            onSubmit={handleSubmit}
            id="upload-form-documents"
        >
            <FilePicker.FilePicker
                onDrop={onDrop}
                disabled={uploadMutation.isLoading}
            >
                <FilePicker.FilePickerIcon />
                <FilePicker.FilePickerMeta description="Click to upload documents" />
            </FilePicker.FilePicker>

            {selectedFiles && selectedFiles.length > 0 && (
                <FilePicker.FilePickerSelectedFiles
                    selectedFiles={selectedFiles}
                    handleRemoveSelectedFile={handleRemoveSelectedFile}
                    wrapperClassName="grid grid-cols-1 grid-flow-row flex-row gap-[--spacing-items]"
                />
            )}
        </form>
    );
}
